"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import Image from "next/image"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { ThumbsUp, Eye, Share2, MapPin, Calendar, Leaf, ArrowLeft, ArrowRight, ClipboardList } from "lucide-react"
import { VoteButtons, type VoteUpdate } from "./vote-buttons"
import { ShareButtons } from "./share-buttons"
import { formatDistanceToNow } from "date-fns"
import { ReportPhotoDialog } from "./report-photo-dialog"
import { communityAnalytics } from "@/lib/analytics"
import {
  COMMUNITY_PROJECT_TIMEFRAME_LABELS,
  COMMUNITY_PROJECT_TYPE_LABELS,
  isCommunityProjectTimeframe,
  isCommunityProjectType,
} from "@/lib/community/story"

export interface PhotoDetailData {
  id: string
  slug: string | null
  image_url: string
  thumbnail_url?: string | null
  title: string
  description?: string | null
  score: number
  upvotes: number
  downvotes: number
  grass_type?: string | null
  zip_code?: string | null
  project_type?: string | null
  photo_taken_month?: string | null
  project_timeframe?: string | null
  starting_point?: string | null
  steps_taken?: string | null
  outcome?: string | null
  created_at: string
  view_count?: number | null
  share_count?: number | null
  social_handle?: string | null
}

interface PhotoDetailProps {
  photo: PhotoDetailData
  relatedPhotos?: RelatedPhoto[]
  grassGuideHref?: string
  imageUnoptimized?: boolean
}

export interface RelatedPhoto {
  id: string
  slug: string | null
  image_url: string
  thumbnail_url?: string | null
  title: string
  score: number
  grass_type?: string | null
  zip_code?: string | null
}

function formatPhotoMonth(value: string | null | undefined): string | null {
  if (!value || !/^\d{4}-\d{2}/.test(value)) return null
  const date = new Date(`${value.slice(0, 7)}-01T00:00:00.000Z`)
  if (Number.isNaN(date.getTime())) return null
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date)
}

export function PhotoDetail({
  photo,
  relatedPhotos = [],
  grassGuideHref,
  imageUnoptimized = false,
}: PhotoDetailProps) {
  const [voteTotals, setVoteTotals] = useState({
    score: photo.score,
    upvotes: photo.upvotes,
    downvotes: photo.downvotes,
  })

  useEffect(() => {
    setVoteTotals({
      score: photo.score,
      upvotes: photo.upvotes,
      downvotes: photo.downvotes,
    })
  }, [photo.id, photo.score, photo.upvotes, photo.downvotes])

  useEffect(() => {
    communityAnalytics.photoOpened(photo.id, 'detail')

    fetch(`/api/community/photos/${photo.id}/view`, { method: 'POST' }).catch(() => {})
  }, [photo.id])

  const timeAgo = formatDistanceToNow(new Date(photo.created_at), { addSuffix: true })
  const photoMonth = formatPhotoMonth(photo.photo_taken_month)
  const projectType = isCommunityProjectType(photo.project_type)
    ? COMMUNITY_PROJECT_TYPE_LABELS[photo.project_type]
    : null
  const projectTimeframe = isCommunityProjectTimeframe(photo.project_timeframe)
    ? COMMUNITY_PROJECT_TIMEFRAME_LABELS[photo.project_timeframe]
    : null
  const hasStory = Boolean(
    photo.starting_point || photo.steps_taken || photo.outcome || projectType || projectTimeframe || photoMonth,
  )

  return (
    <div className="mx-auto max-w-6xl">
      {/* Back Button */}
      <div className="mb-6">
        <Button asChild variant="ghost" className="flex items-center gap-2">
          <Link href="/community">
            <ArrowLeft className="w-4 h-4" />
            Back to Community
          </Link>
        </Button>
      </div>

      <div className="grid min-w-0 gap-6 lg:grid-cols-[1.15fr_0.85fr] lg:gap-10">
        {/* Photo */}
        <div className="min-w-0 space-y-4">
          <div className="relative aspect-4/3 overflow-hidden rounded-[2rem] bg-green-100 shadow-sm">
            <Image
              src={photo.image_url}
              alt={photo.title}
              fill
              className="object-cover"
              sizes="(max-width: 768px) 100vw, 50vw"
              priority
              unoptimized={imageUnoptimized}
            />
          </div>

          {/* Photo Stats */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-green-950/10 bg-white p-4 text-sm text-green-950/65">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <div className="flex items-center gap-1" aria-label={`${photo.view_count || 0} views`}>
                <Eye className="w-4 h-4" aria-hidden="true" />
                <span>{photo.view_count || 0} views</span>
              </div>
              <div className="flex items-center gap-1" aria-label={`${photo.share_count || 0} shares`}>
                <Share2 className="w-4 h-4" aria-hidden="true" />
                <span>{photo.share_count || 0} shares</span>
              </div>
            </div>
            <ReportPhotoDialog photoId={photo.id} photoTitle={photo.title} />
          </div>
        </div>

        {/* Photo Info */}
        <div className="min-w-0 space-y-6">
          {/* Title and Score */}
          <div className="min-w-0 rounded-[2rem] border border-green-950/10 bg-white p-5 shadow-sm sm:p-7">
            <p className="text-xs font-bold uppercase tracking-[0.12em] text-green-700">Community lawn</p>
            <h1 className="font-editorial mt-2 break-words text-4xl font-semibold leading-none text-green-950 sm:text-5xl">{photo.title}</h1>
            <div className="mt-4 flex flex-col items-start gap-2">
              <VoteButtons
                photoId={photo.id}
                currentScore={voteTotals.score}
                onVoteUpdated={(update: VoteUpdate) => {
                  setVoteTotals({
                    score: update.score,
                    upvotes: update.upvotes,
                    downvotes: update.downvotes,
                  })
                }}
              />
              <div className="text-sm text-gray-600">
                {voteTotals.upvotes} upvotes • {voteTotals.downvotes} downvotes
              </div>
            </div>
          </div>

          {hasStory && (
            <section className="overflow-hidden rounded-[2rem] border border-green-950/10 bg-white shadow-sm" aria-labelledby="lawn-story-heading">
              <div className="border-b border-green-950/10 bg-green-50 px-5 py-4 sm:px-7">
                <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-green-700">
                  <ClipboardList className="h-4 w-4" aria-hidden="true" />
                  Homeowner notes
                </p>
                <h2 id="lawn-story-heading" className="font-editorial mt-1 text-3xl font-semibold text-green-950">
                  The lawn story
                </h2>
                <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold text-green-900/75">
                  {projectType && <span className="rounded-full bg-white px-3 py-1.5 ring-1 ring-green-200">{projectType}</span>}
                  {photoMonth && <span className="rounded-full bg-white px-3 py-1.5 ring-1 ring-green-200">Photo: {photoMonth}</span>}
                  {projectTimeframe && <span className="rounded-full bg-white px-3 py-1.5 ring-1 ring-green-200">Timeframe: {projectTimeframe}</span>}
                </div>
              </div>

              <div className="divide-y divide-green-950/10 px-5 sm:px-7">
                {photo.starting_point && (
                  <div className="py-5">
                    <h3 className="text-sm font-bold text-green-950">Starting point</h3>
                    <p className="mt-2 whitespace-pre-line text-sm leading-6 text-gray-700">{photo.starting_point}</p>
                  </div>
                )}
                {photo.steps_taken && (
                  <div className="py-5">
                    <h3 className="text-sm font-bold text-green-950">What they did</h3>
                    <p className="mt-2 whitespace-pre-line text-sm leading-6 text-gray-700">{photo.steps_taken}</p>
                  </div>
                )}
                {photo.outcome && (
                  <div className="py-5">
                    <h3 className="text-sm font-bold text-green-950">How it turned out</h3>
                    <p className="mt-2 whitespace-pre-line text-sm leading-6 text-gray-700">{photo.outcome}</p>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Social Handle */}
          {photo.social_handle && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-800 font-medium">
                📱 {photo.social_handle}
              </p>
            </div>
          )}

          {/* Description */}
          {photo.description && (
            <div>
              <h2 className="font-semibold text-gray-900 mb-2">Description</h2>
              <p className="text-gray-700 leading-relaxed">{photo.description}</p>
            </div>
          )}

          <Separator />

          {/* Metadata */}
          <div className="space-y-4">
            <h2 className="font-semibold text-gray-900">Details</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">Posted {timeAgo}</span>
              </div>

              {photo.zip_code && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-600">
                    ZIP region: {photo.zip_code.substring(0, 3)}
                  </span>
                </div>
              )}
            </div>

            {photo.grass_type && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Leaf className="w-4 h-4 text-gray-500" aria-hidden="true" />
                  <Badge variant="outline">{photo.grass_type}</Badge>
                </div>
                {grassGuideHref && (
                  <Link
                    href={grassGuideHref}
                    className="inline-flex min-h-11 items-center gap-2 rounded-full bg-green-50 px-4 py-2 text-sm font-semibold text-green-800 transition-colors hover:bg-green-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-700 focus-visible:ring-offset-2"
                  >
                    Learn how to care for {photo.grass_type}
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                )}
              </div>
            )}
          </div>

          <Separator />

          {/* Share */}
          <div>
            <h2 className="font-semibold text-gray-900 mb-3">Share this lawn</h2>
            <ShareButtons
              photoSlug={photo.slug || photo.id}
              photoId={photo.id}
              title={photo.title}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button asChild variant="outline" className="w-full sm:flex-1">
              <Link href="/community/submit">
                <Leaf className="w-4 h-4 mr-2" />
                Share your lawn
              </Link>
            </Button>
            <Button asChild variant="outline" className="w-full sm:flex-1">
              <Link href="/community/leaderboard">
                <ThumbsUp className="w-4 h-4 mr-2" />
                View leaderboard
              </Link>
            </Button>
          </div>
        </div>
      </div>

      {/* Related Photos Section */}
      {relatedPhotos.length > 0 && (
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            More Lawns Like This
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {relatedPhotos.map((relatedPhoto) => (
              <Link
                key={relatedPhoto.id}
                href={`/community/${relatedPhoto.slug || relatedPhoto.id}`}
                className="group"
              >
                <Card className="overflow-hidden transition-all hover:shadow-lg hover:-translate-y-1">
                  <div className="relative aspect-square">
                    <Image
                      src={relatedPhoto.thumbnail_url || relatedPhoto.image_url}
                      alt={relatedPhoto.title}
                      fill
                      loading="lazy"
                      className="object-cover transition-transform group-hover:scale-105"
                      sizes="(max-width: 768px) 50vw, 33vw"
                    />
                    <div className="absolute top-2 right-2">
                      <Badge variant={relatedPhoto.score > 0 ? "default" : "secondary"} className="text-xs">
                        {relatedPhoto.score > 0 ? "+" : ""}{relatedPhoto.score}
                      </Badge>
                    </div>
                    {relatedPhoto.grass_type && (
                      <div className="absolute top-2 left-2">
                        <Badge variant="outline" className="bg-white/90 text-gray-800 text-xs">
                          {relatedPhoto.grass_type}
                        </Badge>
                      </div>
                    )}
                  </div>
                  <CardContent className="p-3">
                    <h3 className="font-medium text-sm line-clamp-2 group-hover:text-green-600 transition-colors">
                      {relatedPhoto.title}
                    </h3>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
